import type { Labyrinth } from './Labyrinth';
import type { ExplorationData } from './ExplorationData';
import type { Entity, Enemy, ShielderEnemy } from './Entity';
import type { ComputedRobotStats } from '../upgrades/StatsSystem';
import type { UpgradeDef } from '../upgrades/UpgradeData';
import type { WeaponDef, WeaponCategory } from '../upgrades/WeaponData';
import type { ScoredItem } from '../upgrades/Quality';
import type { AchievementSystem } from '../achievements/AchievementSystem';
import {
  type Projectile, type VfxEffect, type EnemyProjectile, type LingerZone,
  createMuzzleFlash, createMeleeSlash, createEnemyBomb,
} from './Projectile';
import { hasStatusEffect } from './StatusEffect';
import { distSq } from './mathUtils';
import {
  BASE_ROBOT_DAMAGE,
  BASE_TURRET_TURN_SPEED,
  CARDINAL_DIRS,
} from './RobotConstants';
import {
  tilesConnected,
  hasLineOfSight as rayHasLineOfSight,
  canSeeTileForReveal,
} from './RobotVision';
import { RobotNavigator } from './RobotPathing';
import { RobotWeaponry } from './RobotWeapons';
import { RobotTurretry } from './RobotTurret';
import {
  findNearest,
  updateExploring, updateCombat, updateLooting, updateMovingToMarker,
} from './RobotStates';
import type { WeaponStatusEffect } from '../upgrades/WeaponData';
import type { SoundSystem } from '../audio/SoundSystem';
import type { SwarmManager } from './SwarmManager';

/** Lightweight weapon info for rendering — only what the turret graphic needs. */
export interface WeaponSlotRenderInfo {
  category: WeaponCategory;
  tier: number;
  /** 0 = ready to fire, 1 = just fired / full cooldown. Used for charge-up visuals. */
  cooldownProgress: number;
}

/** Render info for an independently-rotating side hardpoint */
export interface SideHardpointRenderInfo {
  worldAngle: number;
  slot: WeaponSlotRenderInfo | null;
  slotIndex: number;
}

/** Weapon info passed from UpgradeSystem at deploy time */
export interface EquippedWeaponInfo {
  id: string;
  category: WeaponCategory;
  tier: number;
  damage: number;
  attackSpeed: number;
  range: number;
  aoeRadius: number;
  energyCost: number;
  burstCount: number;
  knockback: number;
  // Unique weapon behavior fields
  pierceCount: number;
  homingStrength: number;
  projectileCount: number;
  spreadAngle: number;
  speedMult: number;
  fragmentCount: number;
  pullStrength: number;
  lingerDuration: number;
  lingerDps: number;
  damageFalloff: boolean;
  doubleStrike: boolean;
  bonusDamageMult: number;
  splashRadius: number;
  statusEffect: WeaponStatusEffect | null;
}

export type RobotState = 'exploring' | 'combat' | 'looting' | 'moving_to_marker';

export class Robot {
  x: number;
  y: number;
  angle: number;

  energy: number;
  maxEnergy: number;

  // Public state for UI
  state: RobotState = 'exploring';
  /** When true, the robot ignores enemies and does not enter combat. */
  passive = false;
  target: Entity | null = null;
  combatTimer = 0;
  lootProgress = 0;
  xpGained = 0;
  killCount = 0;
  lootBag: { def: UpgradeDef | WeaponDef; item: ScoredItem }[] = [];  // items found this deploy
  qualityBonus = 0;  // building bonus: loot-enhancer flattens score distribution

  /**
   * Turret + side-hardpoint subsystem. Owns aim state and targeting for
   * both the main turret and the two side hardpoints. Assigned in the
   * constructor; see `RobotTurret.ts`. Chassis / rendering / weapon code
   * reads turret state through the delegating getters below.
   */
  turretry!: RobotTurretry;

  /** Current main-turret angle (world space). Delegates to `turretry`. */
  get turretAngle(): number { return this.turretry.turretAngle; }
  /** Side-hardpoint world angles (length-2 array). Delegates to `turretry`. */
  get sideHardpointAngles(): number[] { return this.turretry.sideHardpointAngles; }
  /** Per-hardpoint regular-enemy targets. Read by `RobotWeaponry`. */
  get sideHardpointEnemyTargets(): (Enemy | null)[] { return this.turretry.sideHardpointEnemyTargets; }
  /** Per-hardpoint swarm-index targets (-1 = none). Read by `RobotWeaponry`. */
  get sideHardpointSwarmTargets(): number[] { return this.turretry.sideHardpointSwarmTargets; }

  // Weapon system
  /**
   * Weapon subsystem — owns the equipped roster, per-slot cooldown /
   * burst state, and the damage + projectile-tick logic. Assigned in
   * the constructor; see `RobotWeapons.ts`.
   */
  weaponry!: RobotWeaponry;

  /** Optional reference to the global achievement system. Set by
   *  LabyrinthState right after construction so that kill/damage/loot
   *  events attributed inside the weaponry subsystem can update
   *  persistent counters without plumbing the manager through every arg. */
  achievements: AchievementSystem | null = null;

  /** Per-run damage accumulator and low-HP tripwire. Set by damage sites
   *  (Robot.ts, RobotStates.ts, RobotWeapons.ts, LabyrinthState.ts) and read
   *  at commitRunEndStats to decide Untouchable / Hanging by a Wire /
   *  Phoenix Protocol achievements. Reset by Robot construction (one Robot
   *  per deployment) so they're effectively per-run scratch. */
  damageTakenThisRun = 0;
  lowHpEverThisRun = false;
  maxDistFromSpawnThisRun = 0;

  // Projectiles & VFX — public so LabyrinthState can render them.
  // Ownership is shared with `this.weaponry` via reference passed at
  // construction; both sides mutate the same arrays.
  projectiles: Projectile[] = [];
  enemyProjectiles: EnemyProjectile[] = [];
  lingerZones: LingerZone[] = [];
  vfx: VfxEffect[] = [];
  // Reusable scratch arrays (avoids per-frame allocation)
  private _aliveEnemies: Enemy[] = [];
  private _aliveShielders: ShielderEnemy[] = [];

  /**
   * Frame-valid view of alive enemies, populated at the top of `update()`.
   * LabyrinthState reuses this instead of iterating `entities` a second time
   * — the two used to do the same pass independently every frame.
   * Only valid after `update()` has run this frame.
   */
  get aliveEnemies(): readonly Enemy[] { return this._aliveEnemies; }

  /** Frame-valid view of alive shielder enemies — consumed by the damage path. */
  get aliveShielders(): readonly ShielderEnemy[] { return this._aliveShielders; }

  // Combat log — recent events for UI display
  combatLog: { text: string; color: number; time: number }[] = [];
  private logTime = 0;
  private static readonly MAX_LOG_LINES = 40;

  // Floating damage texts — public for rendering
  floatingTexts: { text: string; color: number; x: number; y: number; vx: number; life: number; maxLife: number; big: boolean }[] = [];

  // Player-placed marker
  marker: [number, number] | null = null;

  private labyrinth: Labyrinth;
  private exploration: ExplorationData;
  /** Public so state-machine helpers can scan entities without a getter call per frame. */
  entities: Entity[] = [];

  // Swarm system — parallel targeting for swarm enemies
  /** Public so `RobotWeaponry` can fire at swarm enemies directly. */
  swarmManager: SwarmManager | null = null;
  swarmTargetIdx = -1; // >= 0 when targeting a swarm enemy

  /**
   * Navigation subsystem — owns spline path state, stuck detection,
   * wander phase, and evasive strafe state. Assigned in the constructor
   * once the physics stats are resolved. All path / movement logic
   * lives in `RobotPathing.ts`; Robot delegates through this reference.
   */
  /** Public so `RobotWeaponry.applyDamageToEnemy` can clear the path on target death. */
  nav!: RobotNavigator;

  // Vision throttling — re-run revealVision on tile change, or every 0.5s as a fallback
  // to keep the frontier updated even when the robot is stationary
  private lastRevealTileX = -999;
  private lastRevealTileY = -999;
  private revealFallbackTimer = 0;

  // Per-frame nearest-entity cache — computed once per update(), used by all state methods
  /**
   * Per-frame nearest-entity cache populated once at the top of
   * `update()` and consumed by the state-machine helpers in
   * `RobotStates.ts`. Public so those free functions can read it
   * without paying for a findNearest call per state transition.
   */
  _cachedNearestEnemy: Entity | null = null;
  _cachedNearestLoot: Entity | null = null;

  // Numeric visited set — mirrors exploration.visited with integer keys (y*width+x) to avoid
  // string parsing and template-literal allocations in the exploration hot loops
  private visitedNumeric = new Set<number>();

  // Base tile — loot-distance calculations reference home position.
  /** Public so `RobotWeaponry.applyDamageToEnemy` can compute loot distance. */
  baseX: number;
  baseY: number;

  // Computed stats (from StatsSystem + UpgradeSystem)
  /** Exploration / looting movement speed. Public so state helpers can use it. */
  speed: number;
  /** Combat approach speed. Public so state helpers can use it. */
  combatApproachSpeed: number;
  private energyDrain: number;
  visionRadius: number;
  detectionRadius: number;
  /** Fallback melee cadence when no weapons equipped. Used by state helpers. */
  attackSpeed: number;
  /** Fallback melee damage when no weapons equipped. Used by state helpers. */
  weaponDamage: number;
  damageReduction: number;
  maxStructure: number;
  structure: number;   // current HP
  /** Sound system. Public so state helpers can play enemy-attack SFX. */
  sound: SoundSystem | null = null;

  constructor(
    labyrinth: Labyrinth,
    exploration: ExplorationData,
    startX: number,
    startY: number,
    robotStats?: ComputedRobotStats,
    initialAngle?: number,
    equippedWeapons?: EquippedWeaponInfo[],
    weaponSlotCount?: number,
    sound?: SoundSystem,
  ) {
    this.labyrinth = labyrinth;
    this.exploration = exploration;
    this.x = startX + 0.5;
    this.y = startY + 0.5;
    this.baseX = startX;
    this.baseY = startY;
    const expeditionAngle = initialAngle ?? Math.random() * Math.PI * 2;
    this.angle = expeditionAngle;

    // Apply computed stats
    const s = robotStats;
    this.speed = s?.speed ?? 3.5;
    this.combatApproachSpeed = s?.combatApproachSpeed ?? 2.5;
    const turnSpeed = s?.turnSpeed ?? (Math.PI * 2.2);
    this.energyDrain = s?.energyDrain ?? 0.4;
    this.visionRadius = s?.visionRadius ?? 4;
    this.detectionRadius = s?.detectionRadius ?? 7;
    const turretTurnSpeed = (s?.turretSpeed ?? 1) * BASE_TURRET_TURN_SPEED;
    this.attackSpeed = s?.attackSpeed ?? 0.8;
    this.weaponDamage = BASE_ROBOT_DAMAGE * (s?.weaponDamageMult ?? 1);
    this.damageReduction = s?.damageReduction ?? 0;
    this.maxStructure = s?.maxStructure ?? 50;
    this.structure = this.maxStructure;

    this.maxEnergy = s?.maxEnergy ?? 100;
    this.energy = this.maxEnergy;

    // Equipped weapons — normalised list + effective slot count.
    const resolvedWeapons = equippedWeapons ?? [];
    const resolvedSlotCount = weaponSlotCount ?? Math.max(1, resolvedWeapons.length);
    this.sound = sound ?? null;

    // Weapon subsystem owns the roster, cooldowns, and damage application.
    // `projectiles` / `enemyProjectiles` / `lingerZones` / `vfx` are shared
    // by reference so LabyrinthState's renderer keeps working unchanged.
    this.weaponry = new RobotWeaponry({
      weapons: resolvedWeapons,
      weaponSlotCount: resolvedSlotCount,
      projectiles: this.projectiles,
      enemyProjectiles: this.enemyProjectiles,
      lingerZones: this.lingerZones,
      vfx: this.vfx,
      sound: this.sound,
      labyrinth,
    });

    this.turretry = new RobotTurretry({ turretTurnSpeed });

    // Backfill visitedNumeric from persisted exploration.visited so the numeric set
    // is in sync on subsequent deploys (exploration data persists across runs)
    const w = labyrinth.width;
    for (const key of exploration.visited) {
      const comma = key.indexOf(',');
      const vx = parseInt(key, 10);
      const vy = parseInt(key.slice(comma + 1), 10);
      this.visitedNumeric.add(vy * w + vx);
    }

    this.nav = new RobotNavigator({
      labyrinth,
      exploration,
      visitedNumeric: this.visitedNumeric,
      baseX: startX,
      baseY: startY,
      expeditionAngle,
      combatApproachSpeed: this.combatApproachSpeed,
      turnSpeed,
      startX: this.x,
      startY: this.y,
    });

    this.revealVision();
  }

  /** Weapon slot data for turret rendering (category + tier per slot, null for empty). */
  get weaponRenderSlots(): (WeaponSlotRenderInfo | null)[] {
    return this.weaponry.getWeaponRenderSlots();
  }

  /** Indices of weapon slots that are independently-rotating side hardpoints. */
  getSideSlotIndices(): number[] {
    const n = this.weaponry.weaponSlotCount;
    if (n >= 4) return [2, 3]; // outer pair
    if (n >= 3) return [1, 2]; // flankers
    return [];
  }

  /** Whether a given slot is a side hardpoint (rotates independently). */
  isSideHardpoint(slotIndex: number): boolean {
    const sides = this.getSideSlotIndices();
    return sides[0] === slotIndex || sides[1] === slotIndex;
  }

  /** Render info for side hardpoints — world angles + slot data. */
  get sideHardpointRenderInfo(): SideHardpointRenderInfo[] {
    const indices = this.getSideSlotIndices();
    if (indices.length === 0) return [];
    const result: SideHardpointRenderInfo[] = [];
    for (let h = 0; h < indices.length; h++) {
      const si = indices[h]!;
      const slot = this.weaponry.getHardpointSlotRender(si);
      result.push({ worldAngle: this.sideHardpointAngles[h]!, slot, slotIndex: si });
    }
    return result;
  }

  setEntities(entities: Entity[]) {
    this.entities = entities;
  }

  setSwarmManager(manager: SwarmManager) {
    this.swarmManager = manager;
  }

  /** Spawn a floating damage/status text at world position. Public so `RobotWeaponry` can emit hit/XP text. */
  floatText(text: string, color: number, x: number, y: number, big = false) {
    const vx = (Math.random() - 0.5) * 0.8; // slight random horizontal drift

    // Stagger vertically: offset upward if recent texts overlap this position
    const staggerStep = 0.45; // vertical offset per overlapping text (tile units)
    const proximityX = 0.6;   // how close in X to count as overlapping
    const proximityY = 0.6;   // how close in Y to count as overlapping
    let yOffset = 0;
    for (const ft of this.floatingTexts) {
      if (ft.life > ft.maxLife * 0.5 && Math.abs(ft.x - x) < proximityX && Math.abs(ft.y - (y - yOffset)) < proximityY) {
        yOffset += staggerStep;
      }
    }

    this.floatingTexts.push({ text, color, x, y: y - yOffset, vx, life: 1.2, maxLife: 1.2, big });
    if (this.floatingTexts.length > 30) this.floatingTexts.shift();
  }

  /** Add a line to the combat log. Public so `RobotWeaponry` can report hits/loot/kills. */
  log(text: string, color: number = 0x8899aa) {
    this.combatLog.push({ text, color, time: this.logTime });
    if (this.combatLog.length > Robot.MAX_LOG_LINES) {
      this.combatLog.shift();
    }
  }

  /** Place a marker on the map — robot will navigate there first. */
  setMarker(worldX: number, worldY: number) {
    const tx = Math.floor(worldX);
    const ty = Math.floor(worldY);
    // Only place on walkable, revealed tiles
    if (!this.labyrinth.isWalkable(tx, ty)) return;
    if (!this.exploration.isRevealed(tx, ty)) return;
    this.marker = [worldX, worldY];
    this.state = 'moving_to_marker';
    this.nav.clearPath();
  }

  /**
   * Sample the current spline path as a polyline for rendering.
   * Thin delegate to `RobotNavigator.getPathPoints` — kept on Robot so
   * `LabyrinthState` doesn't have to reach into `robot.nav` directly.
   *
   * When moving to a manual marker via direct line-of-walk, the navigator
   * drops the spline and drives straight; fall back to a two-point polyline
   * so the GPS trail still renders instead of vanishing.
   */
  getPathPoints(stepDist = 0.5): [number, number][] {
    const pts = this.nav.getPathPoints(this.x, this.y, stepDist);
    if (pts.length >= 2) return pts;
    if (this.state === 'moving_to_marker' && this.marker) {
      return [[this.x, this.y], [this.marker[0], this.marker[1]]];
    }
    return pts;
  }

  /** Remove the current marker */
  clearMarker() {
    this.marker = null;
    if (this.state === 'moving_to_marker') {
      this.state = 'exploring';
      this.nav.clearPath();
    }
  }

  /** Current movement speed (tiles/sec), for animation purposes */
  currentSpeed = 0;

  get tileX(): number { return Math.floor(this.x); }
  get tileY(): number { return Math.floor(this.y); }
  get displayX(): number { return this.x; }
  get displayY(): number { return this.y; }

  update(dt: number) {
    if (this.energy <= 0) return;

    // Populate alive enemies once per frame — used by fireSingleShot + updateProjectilesAndVfx
    const aliveEnemies = this._aliveEnemies;
    aliveEnemies.length = 0;
    const aliveShielders = this._aliveShielders;
    aliveShielders.length = 0;
    for (const e of this.entities) {
      if (e.type === 'enemy' && e.alive) {
        const enemy = e as Enemy;
        aliveEnemies.push(enemy);
        if (enemy.enemyKind === 'shielder') aliveShielders.push(enemy);
      }
    }

    this.nav.tickWander(dt);
    this.logTime += dt;
    const tx = this.tileX;
    const ty = this.tileY;
    this.revealFallbackTimer += dt;
    if (tx !== this.lastRevealTileX || ty !== this.lastRevealTileY || this.revealFallbackTimer >= 0.5) {
      this.lastRevealTileX = tx;
      this.lastRevealTileY = ty;
      this.revealFallbackTimer = 0;
      this.revealVision();
    }

    // Charger contact damage + bomber independent firing — single pass over alive enemies
    for (const enemy of aliveEnemies) {
      if (enemy.enemyKind === 'charger' && enemy.chargeState === 'charging') {
        // 0.8 unit contact radius — squared-dist check skips the sqrt.
        if (distSq(enemy.x, enemy.y, this.x, this.y) < 0.64) {
          const chargerDmg = enemy.damage * (1 - this.damageReduction);
          this.structure -= chargerDmg;
          this.damageTakenThisRun += chargerDmg;
          if (this.structure < this.maxStructure * 0.1) this.lowHpEverThisRun = true;
          this.vfx.push(createMeleeSlash(this.x, this.y, 0xffaa00));
          this.floatText(`${Math.round(enemy.damage)}`, 0xff4444, this.x, this.y - 0.3);
          enemy.chargeState = 'recovering';
          enemy.chargeTimer = 1.5;
        }
      } else if (enemy.enemyKind === 'bomber') {
        if (hasStatusEffect(enemy, 'stun')) continue;
        const dSq = distSq(enemy.x, enemy.y, this.x, this.y);
        const rangeSq = enemy.attackRange * enemy.attackRange;
        if (dSq > rangeSq || dSq > 196 /* 14^2 */) continue;
        enemy.attackTimer += dt;
        if (enemy.attackTimer >= enemy.attackSpeed) {
          enemy.attackTimer -= enemy.attackSpeed;
          this.enemyProjectiles.push(
            createEnemyBomb(enemy.x, enemy.y, this.x, this.y, enemy.damage, enemy.bombAoeRadius),
          );
          this.vfx.push(createMuzzleFlash(enemy.x, enemy.y, 0x9933cc));
          void this.sound?.playSound('/sound/ballistic-weapon.mp3', 0.3);
        }
      }
    }

    // Compute nearest entities once per frame — used by all state methods below
    if (this.state !== 'combat') {
      this._cachedNearestEnemy = findNearest(this, 'enemy');
      this._cachedNearestLoot = (this.state === 'exploring') ? findNearest(this, 'loot') : null;
    }

    switch (this.state) {
      case 'exploring':        updateExploring(this, dt);      break;
      case 'combat':           updateCombat(this, dt);         break;
      case 'looting':          updateLooting(this, dt);        break;
      case 'moving_to_marker': updateMovingToMarker(this, dt); break;
    }

    this.turretry.updateTurret(this, dt);
    this.turretry.updateSideHardpoints(this, dt);
    this.weaponry.updateProjectilesAndVfx(this, dt);
    this.updateFloatingTexts(dt);

    this.energy -= this.energyDrain * dt;
    if (this.energy < 0) this.energy = 0;
  }



  private updateFloatingTexts(dt: number) {
    for (let i = this.floatingTexts.length - 1; i >= 0; i--) {
      const ft = this.floatingTexts[i]!;
      ft.life -= dt;
      ft.y -= dt * 1.2; // float upward (in tile coords)
      ft.x += ft.vx * dt; // horizontal drift
      ft.vx *= 0.96; // dampen drift
      if (ft.life <= 0) this.floatingTexts.splice(i, 1);
    }
  }

  /**
   * Public LOS wrapper — thin delegate to `rayHasLineOfSight` from
   * `RobotVision`. Kept as an instance method so external callers
   * (LabyrinthState's visibility pass) don't have to thread the
   * labyrinth reference themselves.
   */
  hasLineOfSight(tx: number, ty: number): boolean {
    return rayHasLineOfSight(this.labyrinth, this.x, this.y, tx, ty);
  }

  // =======================================================
  // Vision
  // =======================================================
  private revealVision() {
    const cx = this.tileX;
    const cy = this.tileY;
    const r = this.visionRadius;
    const { visited, frontier, unreachable } = this.exploration;

    const ri = Math.ceil(r);

    // Always reveal the robot's own tile
    this.exploration.revealTile(cx, cy);

    for (let dy = -ri; dy <= ri; dy++) {
      for (let dx = -ri; dx <= ri; dx++) {
        if (dx * dx + dy * dy > r * r) continue;
        const x = cx + dx;
        const y = cy + dy;

        // Only reveal tiles the robot has line of sight to (not through walls).
        // Wall tiles are revealed if all tiles before them are clear.
        if (!canSeeTileForReveal(this.labyrinth, this.x, this.y, x, y)) continue;

        this.exploration.revealTile(x, y);

        const key = `${x},${y}`;
        if (!visited.has(key)) {
          visited.add(key);
          this.visitedNumeric.add(y * this.labyrinth.width + x);
          frontier.delete(key);
          unreachable.delete(key);

          if (this.labyrinth.isWalkable(x, y)) {
            for (const [ddx, ddy] of CARDINAL_DIRS) {
              const nx = x + ddx;
              const ny = y + ddy;
              const nKey = `${nx},${ny}`;
              if (
                this.labyrinth.isWalkable(nx, ny) &&
                !visited.has(nKey) &&
                tilesConnected(this.labyrinth, x, y, nx, ny)
              ) {
                frontier.add(nKey);
              }
            }
          }
        }
      }
    }
  }

}
